import { Injectable } from '@angular/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { UpsertRepresentative } from '../representatives/representatives.service';

export type Credit = {
  remaining: number;
  used: number;
  currency_iso: string;
};

export type ClientCreditHistoryItem = {
  date: string | null;
  label: string;
  amount: string | null;
};

export type ClientCreditTransferItem = {
  id: string;
  amount: number;
  currency_iso: string;
  state: string;
  created_at: string | null;
};

export type ClientDataTableItem = {
  id: string;
  client_reference_id: string;
  name: string;
  tax_number: string | null;
  open_cases: number;
  closed_cases: number;
  party_type_type: 'org' | 'ind' | null;
  party_type_id: number | null;
  default_legal_relationship_id: number;
  default_claim_type_id: number;
  pwc_sync_from: string | null;
  can_start_new_pwc_sync: boolean;
  is_deletable:
  true
  | 'client-has-users'
  | 'has-started-cases';
  auto_start_hard_2: boolean;
  uses_credits: boolean;
  credits: Credit[];
  address: {
    country: string | null;
    postcode: string | null;
    settlement: string | null;
    street: string | null;
  };
  bankAccount: {
    account_number: string | null;
    iban: string | null;
    bank_name: string | null;
    account_holder_name: string | null;
    bic: string | null;
    lock_bank_account?: boolean;
  };
  product_discounts: {
    fee: {
      product_type: string;
      currency_iso: string;
      label: string;
    };
    discount_type: 'amount' | 'percentage';
    discount: string;
  }[];
  partners: {
    name: string;
  }[];
  users: {
    email: string;
  }[];
};

export type ClientDataTableParams = {
  client_reference_id?: string;
  name?: string;
  tax_number?: string;
  page?: number;
  per_page?: number;
  sort_by?: 'name'
  | 'tax_number'
  | 'open_cases'
  | 'closed_cases';
  sort_direction?: 'asc' | 'desc';
};

export type ClientDataTableHeader = {
  title: string;
  align: 'start' | 'center';
  key?: keyof ClientDataTableItem;
};

export type ClientDataTableResponse = {
  headers: ClientDataTableHeader[];
  sortBy: unknown[];
  data: ClientDataTableItem[];
  pagination: {
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
  };
};

export type ListClientsParams = {
  name?: string;
};

export type ListClientItem = {
  uuid: string;
  name: string;
};

export type ListClientsResponse = {
  clients: ListClientItem[];
};

export type UpdateCommonClientParams = {
  party_type_id: null | number;
  default_legal_relationship_id?: number;
  default_claim_type_id?: number;
  pwc_sync_from?: string;
  email?: null | string;
  phone?: null | string;
  name?: string;
  address?: null | {
    country_id: number | null;
    settlement: string | null;
    postcode: string | null;
    street: string | null;
  };
  bankAccount?: null | {
    bic?: null | string;
    bank_name?: null | string;
    account_number?: null | string;
    iban?: null | string;
    account_holder_name: string;
  };
  lock_bank_account?: boolean;
  tax_number?: string | null;
  client_data?: {
    birth_date?: string | null;
    birth_place?: string | null;
    mother_name?: string | null;
    registration_number?: string | null;
  };
};

export type UpdateOrgClientParams = UpdateCommonClientParams & {
  lock_representatives?: 1 | 0;
  representatives?: UpsertRepresentative[];
};

export type UpdateIndClientParams = UpdateCommonClientParams;

export type UpdateClientParams = UpdateOrgClientParams | UpdateIndClientParams;

export type CreateClientParams = {
  name: string;
  tax_number: string;
  party_type_id: number;
  address: {
    country: string;
    settlement: string;
    postcode: string;
    street: string;
  };
};

export type ClientDataTableDetailsResponse = {
  client: {
    bank_account: null | {
      id: number;
      account_holder_name: string;
      account_number: null | string;
      iban: null | string;
      bic: null | string;
      bank_name: null | string;
    };
    address: null | string;
    credit_history: ClientCreditHistoryItem[];
    credit_transfers: ClientCreditTransferItem[];
  };
  users: {
    name: string;
    email: string;
  }[];
};

export type MergeClientsParams = {
  master_client_id: string;
  client_to_merge_id: string;
};

export type MergeClientsResponse = {
  message: string;
  master_client: ClientDataTableItem;
};

@Injectable({
  providedIn: 'root'
})
export class ClientsService {
  async getClientsDataTable(params: ClientDataTableParams): Promise<ClientDataTableResponse> {
    const url = environment.baseUrl + '/api/client-datatable';
    const result = await axios.get<ClientDataTableResponse>(
      url,
      {
        params,
      },
    );

    return result.data;
  }

  async getClientsDataTableDetails(clientId: string): Promise<ClientDataTableDetailsResponse> {
    const url = `${environment.baseUrl}/api/client-datatable/${clientId}/details`;
    const result = await axios.get<ClientDataTableDetailsResponse>(url);

    return result.data;
  }

  async listClients(params: ListClientsParams): Promise<ListClientsResponse> {
    const url = environment.baseUrl + '/api/client';
    const result = await axios.get<ListClientsResponse>(
      url,
      {
        params,
      },
    );

    return result.data;
  }

  async updateClient(clientId: string, params: UpdateClientParams): Promise<ClientDataTableItem> {
    const url = environment.baseUrl + `/api/client/${clientId}`;
    const result = await axios.patch<ClientDataTableItem>(
      url,
      params,
    );

    return result.data;
  }

  async createClient(params: CreateClientParams): Promise<ClientDataTableItem> {
    const url = environment.baseUrl + `/api/client`;
    const result = await axios.post<ClientDataTableItem>(url, params);

    return result.data;
  }

  async deleteClient(id: string): Promise<void> {
    const url = environment.baseUrl + `/api/client/${id}`;
    await axios.delete<ClientDataTableItem>(url);
  }

  async getClient(id: string): Promise<ListClientItem> {
    const url = environment.baseUrl + `/api/client/${id}`;
    const result = await axios.get<ListClientItem>(url);

    return result.data;
  }

  async mergeClients(params: MergeClientsParams): Promise<MergeClientsResponse> {
    const url = environment.baseUrl + '/api/client/merge';
    const result = await axios.post<MergeClientsResponse>(url, params);
    return result.data;
  }
}
